import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import AppModal from '../../../../../generic/ui/Modal/AppModal'
import ConfirmDialog from '../../../../../generic/ui/ConfirmDialog/ConfirmDialog'
import { GridTable, TableToolbar } from '../../../../../generic/grid-table'
import { GridColumn } from '../../../../../generic/grid-table/types/grid.types'
import CustomerForm from './components/CustomerForm'
import useTableData from '../../../../../generic/universal-table/hooks/useTableData'
import useTableEditing from '../../../../../generic/universal-table/hooks/useTableEditing'
import useTableFilters from '../../../../../generic/universal-table/hooks/useTableFilters'
import { Defaults } from '../../../../../modules/API/Defaults/defaults'
import { IClientItem } from '../../../../../modules/API/Defaults/models/clients'
import { ISellerItem } from '../../../../../modules/API/Defaults/models/seller'
import { customerTableService } from './services/customerTable.service'
import { customerTableColumns } from './configs/customerTable.columns'
import { customerTableFormFields } from './configs/customerTable.form'
import { customerTableAdapter } from './adapters/customerTable.adapter'
import {
  CustomerCreatePayload,
  CustomerUiRow,
  CustomerUpdatePayload,
} from './types/customerTable.types'
import { ColumnDefinition } from '../../../../../generic/universal-table/types/column.types'
import { FormFieldDefinition } from '../../../../../generic/universal-table/types/form.types'
import useExportToExcel from '../../../../../generic/hooks/useExportToExcel'
import columnsToExcel from '../../../../../generic/universal-table/utils/columnsToExcel'
import { handleNotifications } from '../../../../../generic/utils/handleNotifications'
import { MarketingAreaObjectP } from '../../../../../../models/tableOfTables/marketingArea'

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  width: 100%;
  height: 100%;
  padding: 0.5em;
  overflow: hidden;
`

const ErrorMessage = styled.p`
  color: tomato;
  direction: rtl;
  padding: 0.5em 1em;
`

const CustomerTableModule = () => {
  const {
    rows,
    isLoading,
    error,
    createRow,
    updateRow,
    removeRow,
  } = useTableData<CustomerUiRow, CustomerCreatePayload, CustomerUpdatePayload>(
    customerTableService,
    customerTableAdapter.getRowId
  )

  const {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTableEditing<CustomerUiRow>()

  const { searchQuery, filteredRows, handleSearch } = useTableFilters<CustomerUiRow>(rows)

  const [clients, setClients] = useState<IClientItem[]>([])
  const [sellers, setSellers] = useState<ISellerItem[]>([])
  const [marketingAreas, setMarketingAreas] = useState<MarketingAreaObjectP[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sellersRes, clientsRes, marketingAreasRes]: [
          { sellers: ISellerItem[] },
          IClientItem[],
          MarketingAreaObjectP[],
        ] = await Promise.all([
          Defaults.getSellerList(''),
          Defaults.getClientsGo(),
          Defaults.getDataFromTableOfTables(300),
        ])

        setSellers(sellersRes?.sellers ?? [])
        setClients(clientsRes)
        setMarketingAreas(marketingAreasRes)
      } catch (fetchError) {
        handleNotifications('שגיאה בטעינת הנתונים', { type: 'error' })
      }
    }

    fetchData()
  }, [])

  const clientOptions = useMemo(
    () => [{ value: 0, label: 'ללא לקוח' }, ...clients.map((c) => ({ value: c.ClientID, label: c.ClientName }))],
    [clients]
  )

  const sellerOptions = useMemo(
    () => [{ value: 0, label: 'ללא משווק' }, ...sellers.map((s) => ({ value: s.SellerID, label: s.Seller_Desc }))],
    [sellers]
  )

  const marketingAreaOptions = useMemo(
    () => [
      { value: 0, label: 'ללא אזור' },
      ...marketingAreas.map((item) => ({
        value: item.SecondCode,
        label: item.Description?.trim() || String(item.SecondCode),
      })),
    ],
    [marketingAreas]
  )

  const enrichedColumns = useMemo<ColumnDefinition<CustomerUiRow>[]>(
    () =>
      customerTableColumns.map((col) => {
        if (col.key === 'ClientID') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              clientOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        if (col.key === 'ResellerID') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              sellerOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        if (col.key === 'MarketingAreaID') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              marketingAreaOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        return col
      }),
    [clientOptions, sellerOptions, marketingAreaOptions]
  )

  const enrichedFormFields = useMemo<FormFieldDefinition<CustomerUiRow>[]>(
    () =>
      customerTableFormFields.map((field) => {
        if (field.name === 'ClientID') {
          return { ...field, getOptions: () => clientOptions }
        }
        if (field.name === 'ResellerID') {
          return { ...field, getOptions: () => sellerOptions }
        }
        if (field.name === 'MarketingAreaID') {
          return { ...field, getOptions: () => marketingAreaOptions }
        }
        return field
      }),
    [clientOptions, sellerOptions, marketingAreaOptions]
  )

  // Grid view reuses the enriched columns and layers on richer renderers.
  const gridColumns = useMemo<GridColumn<CustomerUiRow>[]>(
    () =>
      enrichedColumns.map((col): GridColumn<CustomerUiRow> => {
        if (col.key === 'CustomerID') return { ...col, cellRendererType: 'num' }
        if (col.key === 'DeliveryPrice') {
          return { ...col, cellRendererType: 'money', currencySuffix: '₪' }
        }
        return col
      }),
    [enrichedColumns]
  )

  const excelColumns = useMemo(() => columnsToExcel(enrichedColumns), [enrichedColumns])
  const { handleExport } = useExportToExcel({ columns: excelColumns, fileName: 'customers', rtl: true })

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleFormSubmit = useCallback(
    async (values: Partial<CustomerUiRow>): Promise<void> => {
      setIsSaving(true)
      try {
        if (editingMode === 'edit' && selectedRow) {
          const id = customerTableAdapter.getRowId(selectedRow)
          const payload = customerTableAdapter.toUpdatePayload({
            ...selectedRow,
            ...values,
          } as CustomerUiRow)
          await updateRow(id, payload)
        } else {
          const payload = customerTableAdapter.toCreatePayload(values as CustomerUiRow)
          await createRow(payload)
        }
        handleCloseForm()
      } finally {
        setIsSaving(false)
      }
    },
    [editingMode, selectedRow, createRow, updateRow, handleCloseForm]
  )

  const formTitle = editingMode === 'edit' ? 'עריכת לקוח' : 'הוספת לקוח חדש'

  return (
    <TableSection>
      {isFormOpen && (
        <AppModal
          title={formTitle}
          controlFunc={handleCloseForm}
          Children={CustomerForm}
          childrenProps={{
            fields: enrichedFormFields,
            initialValues: selectedRow ?? undefined,
            onSubmit: handleFormSubmit,
            onDelete: editingMode === 'edit' ? handleRequestDelete : undefined,
            isLoading: isSaving,
            isDeleting,
            submitLabel: editingMode === 'edit' ? 'עדכן' : 'הוסף',
          }}
          size="md"
        />
      )}

      <ConfirmDialog
        open={isConfirmDeleteOpen}
        title="מחיקת לקוח"
        message="האם למחוק את הלקוח? פעולה זו אינה הפיכה."
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={() =>
          handleConfirmDelete((row) => removeRow(customerTableAdapter.getRowId(row)))
        }
      />

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <GridTable<CustomerUiRow>
        title="לקוחות"
        count={filteredRows.length}
        columns={gridColumns}
        rows={filteredRows}
        getRowId={customerTableAdapter.getRowId}
        isLoading={isLoading}
        onRowClick={handleOpenEdit}
        toolbarActions={
          <TableToolbar
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder="חיפוש לקוח…"
            onExport={() => handleExport(filteredRows)}
            onAdd={handleOpenCreate}
            addLabel="הוסף לקוח"
          />
        }
      />
    </TableSection>
  )
}

export default CustomerTableModule
